import React, { FC, useState, useEffect } from 'react';

import { useInView } from 'react-intersection-observer';
import { keyframes } from '@emotion/react';
import { LucidTheme, mqMin } from '@lucid/theme';

import Box from '../box/box';
import Icon from '../icon/icon';
import Heading from '../heading/heading';
import Link from '../link/link';
import Text from '../text/text';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import VisibleLottie from '../visible-lottie/visible-lottie';
import { BaseCarouselProps, BaseCarouselControlsProps } from './carousel';

export interface CarouselDesktopProps extends BaseCarouselProps, BaseCarouselControlsProps {}

const fade = keyframes({
  '0%': {
    opacity: 0
  },
  '100%': {
    opacity: 1
  }
});

const CarouselDesktop: FC<CarouselDesktopProps> = ({ controlsPosition, cta, items }) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [willManualOverride, setWillManualOverride] = useState(false);
  const [hasTransition, setHasTransition] = useState(false);
  const hasBottomControls = controlsPosition === 'bottom';
  const hasLeftControls = controlsPosition === 'left';
  const hasRightControls = controlsPosition === 'right';
  const selectedItem = items[selectedItemIndex];
  const animation = selectedItem.animation;
  const image = selectedItem.image;
  const hasAnimationData = animation?.data;
  const [inViewRef, isInView] = useInView();

  useEffect(() => {
    const next = (selectedItemIndex + 1) % items.length;
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    if (isInView) {
      timeoutId = setTimeout(() => {
        setHasTransition(true);
        setSelectedItemIndex(next);
        setHasTransition(false);
      }, willManualOverride ? 8000 : 4000);
    }
    if (willManualOverride) {
      setWillManualOverride(false);
    }
    return () => clearTimeout(timeoutId as NodeJS.Timeout);
    // Next line is disabled to prevent useEffect being triggered twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, selectedItemIndex, isInView]);

  return (
    <>
      <Box
        ref={inViewRef}
        css={[
          {
            label: 'carouselDesktop',
            flex: '0 1 67.5%',
            maxWidth: '67.5%'
          },
          hasBottomControls && {
            flex: '0 1 100%',
            maxWidth: '100%'
          },
          hasLeftControls && { order: 1 },
          hasRightControls && {
            flex: '0 1 65%'
          }
        ]}
      >
        {
          hasAnimationData ? (
            <Box
              css={(theme: LucidTheme) => [
                {
                  backgroundColor: theme.colors.coolGrey[0],
                  '.carouselLottie': {
                    height: '620px',
                    background: 'url("https://cdn-cashy-static-assets.lucidchart.com/lucidspark/marketing/pages/home/canvas-grid.svg") center center no-repeat',
                  }
                },
                !hasTransition && {
                  // FIXME: transition animation is not working.
                  animation: `${fade} 500ms ease-in-out`
                }
              ]}
            >
              <VisibleLottie
                className="carouselLottie"
                data={animation?.data!}
              />
            </Box>
          ) :
              (
                <ImageWithPlaceholder
                  alt={image!.imageAlt! || ''}
                  aspectRatio="4:3"
                  css={[
                    {
                      animation: `${fade} 500ms ease-in-out`,
                      '& > .image': {
                        overflow: 'hidden',
                        maxHeight: '676px',
                        maxWidth: '730px',
                        zIndex: 20,
                        margin: 'unset'
                      }
                    },
                  ]}
                  src={image!.imageSrc!}
                />
              )
        }
        {selectedItem.footerText && (
          <Text
            css={(theme: LucidTheme) => ({
              textAlign: 'left',
              [mqMin.tabletSmall]: {
                paddingTop: theme.space.s40,
                height: '110px'
              }
            })}
            variant="paragraphM400"
          >
            {selectedItem.footerText}
          </Text>
        )}
      </Box>
      <Box
        css={(theme: LucidTheme) => [
          {
            flex: '0 1 27.5%',
            flexDirection: 'column',
            alignSelf: 'flex-start'
          },
          hasBottomControls && {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
            marginTop: theme.space[4],
            flex: '0 1 100%'
          },
          hasRightControls && {
            display: 'flex',
            flex: '0 1 30%',
            justifyContent: 'center'
          }
        ]}
      >
        {items.map((item, index) => {
          const { heading, subheading } = item;
          const isSelected = index === selectedItemIndex;
          return (
            <Box
              key={index}
              css={(theme: LucidTheme) => ({
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: theme.space[6],
                '&:hover': {
                  'h2, p': {
                    color: theme.colors.primary
                  },
                  svg: {
                    color: theme.colors.indigo[60]
                  }
                }
              })}
              onClick={() => {
                setWillManualOverride(true);
                setSelectedItemIndex(index);
              }}
            >
              <Box
                css={(theme: LucidTheme) => [
                  {
                    '&:hover': {
                      color: theme.colors.secondary
                    }
                  },
                  {
                    display: 'flex',
                    flexDirection: 'row'
                  },
                  hasLeftControls && {
                    justifyContent: 'flex-end'
                  },
                  !hasBottomControls && {
                    alignItems: 'center'
                  }
                ]}
              >
                <Icon
                  css={(theme: LucidTheme) => [
                    {
                      alignItems: 'center',
                      minWidth: '1rem',
                      order: 0
                    },
                    !hasLeftControls && {
                      marginRight: theme.space[2]
                    },
                    hasLeftControls && {
                      order: 1,
                      marginLeft: theme.space[2]
                    },
                    hasBottomControls && {
                      marginTop: theme.space[2]
                    },
                    isSelected && {
                      color: theme.colors.indigo[60]
                    }
                  ]}
                  type="ArrowRight"
                />
                <Heading
                  as="h2"
                  css={(theme: LucidTheme) => [
                    {
                      marginTop: theme.space[0],
                      marginBottom: theme.space[0],
                      textAlign: 'left'
                    },
                    hasBottomControls && {
                      maxWidth: '10ch'
                    },
                    hasLeftControls && {
                      textAlign: 'right'
                    },
                    isSelected && {
                      color: theme.colors.primary
                    }
                  ]}
                  variant="interactiveM700"
                >
                  {heading}
                </Heading>
              </Box>
              {
                subheading && (
                  <Text
                    css={(theme: LucidTheme) => [
                      {
                        marginBottom: theme.space[0],
                        textAlign: 'left'
                      },
                      hasBottomControls && {
                        maxWidth: '12ch'
                      },
                      !hasLeftControls && {
                        marginLeft: theme.space[4]
                      },
                      hasLeftControls && {
                        textAlign: 'right'
                      }
                    ]}
                  >
                    {subheading}
                  </Text>
                )
              }
            </Box>
          );
        })}
        {cta && (
          <Box
            css={[
              hasLeftControls && {
                textAlign: 'right'
              }
            ]}
          >
            {cta.linkUrl && (
              <Link
                displayAsButton
                to={cta.linkUrl!}
                variant={cta.isSecondary ? 'primaryAlternate' : 'primary'}
              >
                {cta.linkText}
              </Link>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export { CarouselDesktop };
export default CarouselDesktop;
